import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import {
  ImagePlus, Loader2, Upload, Trash2, ExternalLink, ArrowLeft, GripVertical,
  Video, Image as ImageIcon, Globe, Lock, Copy, Plus, X, Pencil,
} from 'lucide-react';
import {
  listGalleries, createGallery, updateGallery, deleteGallery,
  getGallery, addGalleryItem, deleteGalleryItem, reorderGalleryItems,
  uploadFile, resolveFileUrl,
} from '../../services/api';
import { toast } from '../../hooks/use-toast';

const PUBLIC_BASE = 'https://emeraldcorporates.com/gallery/';

const isVideo = (name) => /\.(mp4|mov|webm|m4v|avi|mkv)$/i.test(name || '');
const isImage = (name) => /\.(png|jpe?g|gif|webp|svg|heic|heif)$/i.test(name || '');

// =====================================================================
// LIST
// =====================================================================
export const GalleriesListPage = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await listGalleries(); setItems(r.data || []); }
    catch { toast({ title: 'Failed to load galleries', variant: 'destructive' }); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="admin-galleries-page">
      <div className="flex items-end justify-between gap-4 flex-wrap mb-6">
        <div>
          <span className="text-xs font-semibold tracking-[0.25em] text-[#0f5c3d] uppercase">Media</span>
          <h1 className="mt-2 text-4xl text-[#0b1a2e]" style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 500 }}>Galleries</h1>
          <p className="mt-2 text-sm text-[#0b1a2e]/60">Group photos and videos into showcase-ready collections. Public galleries can be shared as a link.</p>
        </div>
        <button onClick={() => setShowNew(true)} data-testid="new-gallery-btn" className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-[#0f5c3d] text-white text-sm font-medium hover:bg-[#0b1a2e]">
          <ImagePlus className="w-4 h-4" /> New gallery
        </button>
      </div>

      {loading ? (
        <div className="p-16 text-center text-[#0b1a2e]/50"><Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Loading…</div>
      ) : items.length === 0 ? (
        <div className="bg-white border border-black/5 rounded-2xl p-16 text-center">
          <ImageIcon className="w-10 h-10 mx-auto mb-3 text-[#0b1a2e]/20" />
          <div className="text-[#0b1a2e]/60 text-sm">No galleries yet. Create your first — e.g. "CSR 2025" or "Office London".</div>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((g) => (
            <Link key={g.gallery_id} to={`/admin/galleries/${g.gallery_id}`} data-testid="gallery-card"
              className="group block bg-white border border-black/5 rounded-2xl overflow-hidden hover:shadow-md transition">
              <div className="aspect-[4/3] bg-[#0b1a2e]/5 relative">
                {g.cover_url ? (
                  <img src={resolveFileUrl(g.cover_url)} alt={g.title} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-[#0b1a2e]/20"><ImageIcon className="w-10 h-10" /></div>
                )}
                <div className="absolute top-2 right-2 flex gap-1">
                  {g.is_public ? (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-[#0f5c3d] text-white text-[10px] rounded-full font-semibold"><Globe className="w-2.5 h-2.5" /> Public</span>
                  ) : (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-black/70 text-white text-[10px] rounded-full font-semibold"><Lock className="w-2.5 h-2.5" /> Private</span>
                  )}
                </div>
              </div>
              <div className="p-4">
                <div className="text-lg font-medium text-[#0b1a2e] group-hover:text-[#0f5c3d]" style={{ fontFamily: 'Cormorant Garamond, serif' }}>{g.title}</div>
                <div className="text-xs text-[#0b1a2e]/50 mt-1 font-mono">/{g.gallery_id}</div>
                <div className="text-xs text-[#0b1a2e]/60 mt-2">{g.item_count} item{g.item_count !== 1 ? 's' : ''}</div>
              </div>
            </Link>
          ))}
        </div>
      )}

      {showNew && <NewGalleryModal onClose={() => setShowNew(false)} onCreated={(g) => { setShowNew(false); load(); }} />}
    </div>
  );
};

const NewGalleryModal = ({ onClose, onCreated }) => {
  const nav = useNavigate();
  const [form, setForm] = useState({ title: '', gallery_id: '', description: '', is_public: true });
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const g = await createGallery(form);
      toast({ title: 'Gallery created' });
      nav(`/admin/galleries/${g.gallery_id}`);
    } catch (err) {
      toast({ title: 'Create failed', description: err?.response?.data?.detail || '', variant: 'destructive' });
    } finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <form onSubmit={submit} className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md">
        <div className="px-6 py-4 border-b border-black/5 flex items-center justify-between">
          <div className="text-lg font-semibold text-[#0b1a2e]">New gallery</div>
          <button type="button" onClick={onClose} className="p-2 hover:bg-black/5 rounded-lg"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-6 space-y-4">
          <label className="block">
            <span className="block text-xs font-semibold uppercase tracking-widest text-[#0b1a2e]/70 mb-1.5">Header / Title</span>
            <input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="gallery-title" placeholder="e.g. CSR 2025 · School Kits Drive" className="w-full border border-[#0f5c3d]/15 rounded-lg px-3 py-2.5 text-sm" />
          </label>
          <label className="block">
            <span className="block text-xs font-semibold uppercase tracking-widest text-[#0b1a2e]/70 mb-1.5">Gallery ID (public URL slug)</span>
            <div className="flex items-center gap-2 text-xs">
              <span className="text-[#0b1a2e]/50 font-mono">{PUBLIC_BASE}</span>
              <input value={form.gallery_id} onChange={(e) => setForm({ ...form, gallery_id: e.target.value })} data-testid="gallery-slug" placeholder="csr-2025" className="flex-1 border border-[#0f5c3d]/15 rounded px-2 py-1.5 text-sm font-mono" />
            </div>
            <p className="mt-1 text-[10px] text-[#0b1a2e]/50">Leave blank to auto-generate from the title.</p>
          </label>
          <label className="block">
            <span className="block text-xs font-semibold uppercase tracking-widest text-[#0b1a2e]/70 mb-1.5">Description</span>
            <textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="w-full border border-[#0f5c3d]/15 rounded-lg px-3 py-2.5 text-sm resize-y" />
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.is_public} onChange={(e) => setForm({ ...form, is_public: e.target.checked })} className="accent-[#0f5c3d]" />
            Public gallery (accessible at <code className="text-[11px] bg-black/5 rounded px-1 py-0.5">{PUBLIC_BASE}slug</code>)
          </label>
        </div>
        <div className="px-6 py-4 border-t border-black/5 bg-[#f7fbf8] flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg text-sm hover:bg-black/5">Cancel</button>
          <button type="submit" disabled={saving} data-testid="submit-new-gallery" className="inline-flex items-center gap-2 px-5 py-2 rounded-lg bg-[#0f5c3d] text-white text-sm font-medium disabled:opacity-60">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Create
          </button>
        </div>
      </form>
    </div>
  );
};

// =====================================================================
// DETAIL — drag-to-upload + drag-to-sort
// =====================================================================
export const GalleryDetailPage = () => {
  const { gallery_id } = useParams();
  const nav = useNavigate();
  const [g, setG] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dragOverPage, setDragOverPage] = useState(false);
  const [uploading, setUploading] = useState([]); // filenames in-flight
  const [editingMeta, setEditingMeta] = useState(false);
  const [metaForm, setMetaForm] = useState({});
  const [dragItemId, setDragItemId] = useState(null);
  const [overItemId, setOverItemId] = useState(null);
  const fileInputRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await getGallery(gallery_id); setG(r); setMetaForm({ title: r.title, gallery_id: r.gallery_id, description: r.description || '', is_public: r.is_public }); }
    catch { toast({ title: 'Gallery not found', variant: 'destructive' }); nav('/admin/galleries'); }
    finally { setLoading(false); }
  }, [gallery_id, nav]);
  useEffect(() => { load(); }, [load]);

  const handleFiles = async (fileList) => {
    const files = Array.from(fileList).filter((f) => isImage(f.name) || isVideo(f.name));
    if (files.length === 0) { toast({ title: 'No image / video files detected', variant: 'destructive' }); return; }
    setUploading((s) => [...s, ...files.map((f) => f.name)]);
    for (const file of files) {
      try {
        const up = await uploadFile(file);
        await addGalleryItem(gallery_id, {
          kind: isVideo(file.name) ? 'video' : 'image',
          url: up.url, filename: up.filename || file.name, size: file.size, mime_type: file.type || '',
          caption: '',
        });
      } catch (err) {
        toast({ title: `Upload failed: ${file.name}`, variant: 'destructive' });
      }
    }
    setUploading((s) => s.filter((n) => !files.some((f) => f.name === n)));
    load();
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOverPage(false);
    if (e.dataTransfer.files?.length) handleFiles(e.dataTransfer.files);
  };

  const removeItem = async (item) => {
    if (!window.confirm('Remove this item?')) return;
    await deleteGalleryItem(gallery_id, item.id);
    load();
  };

  // Drag-to-reorder
  const reorderTo = async (targetId) => {
    if (!dragItemId || dragItemId === targetId) return;
    const ordered = [...(g.items || [])];
    const from = ordered.findIndex((x) => x.id === dragItemId);
    const to = ordered.findIndex((x) => x.id === targetId);
    if (from < 0 || to < 0) return;
    const [moved] = ordered.splice(from, 1);
    ordered.splice(to, 0, moved);
    setG({ ...g, items: ordered });
    try { await reorderGalleryItems(gallery_id, ordered.map((x) => x.id)); }
    catch { toast({ title: 'Reorder failed', variant: 'destructive' }); load(); }
    setDragItemId(null);
    setOverItemId(null);
  };

  const saveMeta = async () => {
    try {
      const updated = await updateGallery(gallery_id, metaForm);
      toast({ title: 'Gallery updated' });
      setEditingMeta(false);
      if (updated.gallery_id !== gallery_id) nav(`/admin/galleries/${updated.gallery_id}`);
      else load();
    } catch (err) { toast({ title: 'Save failed', description: err?.response?.data?.detail || '', variant: 'destructive' }); }
  };

  const removeGallery = async () => {
    if (!window.confirm(`Delete gallery "${g.title}" and all its items?`)) return;
    await deleteGallery(gallery_id);
    nav('/admin/galleries');
  };

  const publicUrl = `${PUBLIC_BASE}${gallery_id}`;

  if (loading || !g) return <div className="p-16 text-center text-[#0b1a2e]/50"><Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Loading gallery…</div>;

  return (
    <div
      data-testid="gallery-detail-page"
      onDragOver={(e) => { e.preventDefault(); setDragOverPage(true); }}
      onDragLeave={() => setDragOverPage(false)}
      onDrop={onDrop}
      className="relative"
    >
      {dragOverPage && (
        <div className="fixed inset-0 z-40 pointer-events-none flex items-center justify-center bg-[#0f5c3d]/10 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl px-8 py-6 border-4 border-dashed border-[#0f5c3d]">
            <Upload className="w-10 h-10 mx-auto text-[#0f5c3d]" />
            <div className="mt-3 text-lg font-semibold text-[#0b1a2e]">Drop files to upload</div>
            <div className="text-sm text-[#0b1a2e]/60">Images and videos accepted</div>
          </div>
        </div>
      )}

      <div className="mb-6 flex items-center gap-2 text-xs text-[#0b1a2e]/60">
        <Link to="/admin/galleries" className="inline-flex items-center gap-1 hover:text-[#0f5c3d]"><ArrowLeft className="w-3 h-3" /> Galleries</Link>
        <span>/</span>
        <span className="font-mono">{gallery_id}</span>
      </div>

      <div className="bg-white border border-black/5 rounded-2xl p-6 mb-4">
        <div className="flex items-start gap-4 justify-between flex-wrap">
          <div className="flex-1 min-w-[200px]">
            {editingMeta ? (
              <div className="space-y-2">
                <input value={metaForm.title} onChange={(e) => setMetaForm({ ...metaForm, title: e.target.value })} className="w-full border border-[#0f5c3d]/15 rounded px-2 py-1 text-2xl font-medium" style={{ fontFamily: 'Cormorant Garamond, serif' }} />
                <div className="flex items-center gap-1 text-xs">
                  <span className="text-[#0b1a2e]/50 font-mono">{PUBLIC_BASE}</span>
                  <input value={metaForm.gallery_id} onChange={(e) => setMetaForm({ ...metaForm, gallery_id: e.target.value })} className="border border-[#0f5c3d]/15 rounded px-2 py-1 text-xs font-mono" />
                </div>
                <textarea rows={2} value={metaForm.description} onChange={(e) => setMetaForm({ ...metaForm, description: e.target.value })} className="w-full border border-[#0f5c3d]/15 rounded px-2 py-1.5 text-sm" placeholder="Description" />
                <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={metaForm.is_public} onChange={(e) => setMetaForm({ ...metaForm, is_public: e.target.checked })} className="accent-[#0f5c3d]" /> Public</label>
              </div>
            ) : (
              <>
                <h1 className="text-3xl text-[#0b1a2e]" style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 500 }}>{g.title}</h1>
                {g.description && <p className="text-sm text-[#0b1a2e]/60 mt-1">{g.description}</p>}
                <div className="mt-2 flex items-center gap-2 flex-wrap">
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] rounded-full font-semibold ${g.is_public ? 'bg-[#0f5c3d] text-white' : 'bg-black/70 text-white'}`}>
                    {g.is_public ? <><Globe className="w-2.5 h-2.5" /> Public</> : <><Lock className="w-2.5 h-2.5" /> Private</>}
                  </span>
                  {g.is_public && (
                    <button
                      onClick={() => { navigator.clipboard.writeText(publicUrl); toast({ title: 'Public URL copied' }); }}
                      data-testid="copy-public-url"
                      className="inline-flex items-center gap-1 px-2 py-0.5 bg-black/5 hover:bg-black/10 text-[10px] rounded-full font-mono text-[#0b1a2e]"
                    >
                      <Copy className="w-2.5 h-2.5" /> {publicUrl}
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
          <div className="flex items-center gap-2">
            {editingMeta ? (
              <>
                <button onClick={() => setEditingMeta(false)} className="px-3 py-1.5 rounded-lg text-sm hover:bg-black/5">Cancel</button>
                <button onClick={saveMeta} data-testid="save-meta" className="px-4 py-1.5 rounded-lg bg-[#0f5c3d] text-white text-sm">Save</button>
              </>
            ) : (
              <>
                <button onClick={() => setEditingMeta(true)} data-testid="edit-meta" className="p-1.5 rounded-lg hover:bg-black/5" title="Edit"><Pencil className="w-4 h-4" /></button>
                <button onClick={removeGallery} className="p-1.5 rounded-lg text-red-600 hover:bg-red-50" title="Delete gallery"><Trash2 className="w-4 h-4" /></button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Upload dropzone */}
      <div className="mb-4 bg-white border-2 border-dashed border-[#0f5c3d]/30 rounded-2xl p-6 text-center">
        <input ref={fileInputRef} type="file" multiple accept="image/*,video/*" hidden onChange={(e) => handleFiles(e.target.files)} data-testid="gallery-file-input" />
        <Upload className="w-6 h-6 mx-auto text-[#0f5c3d] mb-2" />
        <div className="text-sm text-[#0b1a2e]">
          <strong>Drop files here</strong> or <button onClick={() => fileInputRef.current?.click()} data-testid="gallery-browse-btn" className="text-[#0f5c3d] font-semibold hover:underline">browse</button> to upload images & videos.
        </div>
        {uploading.length > 0 && (
          <div className="mt-3 text-xs text-[#0b1a2e]/60"><Loader2 className="w-3 h-3 animate-spin inline mr-1" /> Uploading {uploading.length} file{uploading.length !== 1 ? 's' : ''}…</div>
        )}
      </div>

      {/* Items grid — draggable */}
      {g.items.length === 0 ? (
        <div className="bg-white border border-black/5 rounded-2xl p-16 text-center">
          <ImageIcon className="w-10 h-10 mx-auto mb-3 text-[#0b1a2e]/20" />
          <div className="text-[#0b1a2e]/60 text-sm">Empty gallery — drop files above or click browse.</div>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
          {g.items.map((it) => (
            <div
              key={it.id}
              data-testid="gallery-item"
              draggable
              onDragStart={() => setDragItemId(it.id)}
              onDragOver={(e) => { e.preventDefault(); setOverItemId(it.id); }}
              onDrop={(e) => { e.stopPropagation(); reorderTo(it.id); }}
              onDragEnd={() => { setDragItemId(null); setOverItemId(null); }}
              className={`group relative bg-white border rounded-lg overflow-hidden ${overItemId === it.id ? 'border-[#0f5c3d] ring-2 ring-[#0f5c3d]/30' : 'border-black/5'} ${dragItemId === it.id ? 'opacity-40' : ''}`}
            >
              <div className="aspect-square bg-[#0b1a2e]/5 relative">
                {it.kind === 'image' ? (
                  <img src={resolveFileUrl(it.url)} alt={it.filename} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-[#0b1a2e]/40 bg-black">
                    <video src={resolveFileUrl(it.url)} className="w-full h-full object-cover" muted preload="metadata" />
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none"><Video className="w-8 h-8 text-white/80" /></div>
                  </div>
                )}
                <div className="absolute top-1 left-1 opacity-0 group-hover:opacity-100 transition">
                  <span className="inline-flex items-center px-1.5 py-0.5 bg-black/60 text-white text-[10px] rounded cursor-move">
                    <GripVertical className="w-3 h-3" />
                  </span>
                </div>
                <button onClick={() => removeItem(it)} className="absolute top-1 right-1 p-1 bg-black/60 hover:bg-red-600 text-white rounded opacity-0 group-hover:opacity-100 transition" data-testid="gallery-item-delete">
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
              <div className="p-1.5 text-[10px] text-[#0b1a2e]/60 truncate font-mono">{it.filename}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const GalleriesPage = () => <GalleriesListPage />;
export default GalleriesPage;
